import React, { useState } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Alert,
	CircularProgress,
	Box,
	Checkbox,
	FormControlLabel,
	SelectChangeEvent
} from "@mui/material";
import { authorizedUsersService } from "../../firebase/authorization/authorizedUsersService";

interface AddAuthorizedUserDialogProps {
	open: boolean;
	onClose: () => void;
	onUserAdded: () => void;
}

interface FormData {
	email: string;
	displayName: string;
	role: "admin" | "user";
	isPartner: boolean;
}

const AddAuthorizedUserDialog: React.FC<AddAuthorizedUserDialogProps> = ({
	open,
	onClose,
	onUserAdded
}) => {
	const [formData, setFormData] = useState<FormData>({
		email: "",
		displayName: "",
		role: "user",
		isPartner: false
	});
	const [error, setError] = useState<string>("");
	const [loading, setLoading] = useState(false);

	const handleChange = (
		e:
			| React.ChangeEvent<
					HTMLInputElement | { name?: string; value: unknown }
			  >
			| SelectChangeEvent<string>
	) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name as string]: value
		}));
	};

	const validateForm = (): boolean => {
		if (!formData.email.trim()) {
			setError("Email is required");
			return false;
		}

		// Basic email validation
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(formData.email)) {
			setError("Please enter a valid email address");
			return false;
		}

		if (!formData.displayName.trim()) {
			setError("Display name is required");
			return false;
		}

		setError("");
		return true;
	};

	const handleSubmit = async () => {
		if (!validateForm()) {
			return;
		}

		setLoading(true);
		try {
			await authorizedUsersService.addUser(
				formData.email,
				formData.displayName,
				formData.role,
				formData.isPartner
			);

			// Reset form and close dialog
			setFormData({
				email: "",
				displayName: "",
				role: "user",
				isPartner: false
			});
			setError("");
			onUserAdded();
			onClose();
		} catch (err) {
			setError(err instanceof Error ? err.message : "Failed to add user");
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		setFormData({
			email: "",
			displayName: "",
			role: "user",
			isPartner: false
		});
		setError("");
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>Add Authorized User</DialogTitle>
			<DialogContent>
				<Box
					sx={{
						pt: 2,
						display: "flex",
						flexDirection: "column",
						gap: 2
					}}
				>
					{error && <Alert severity="error">{error}</Alert>}

					<TextField
						label="Email Address"
						type="email"
						name="email"
						value={formData.email}
						onChange={handleChange}
						fullWidth
						placeholder="user@example.com"
						disabled={loading}
						autoFocus
					/>

					<TextField
						label="Display Name"
						name="displayName"
						value={formData.displayName}
						onChange={handleChange}
						fullWidth
						placeholder="John Doe"
						disabled={loading}
					/>

					<FormControl fullWidth>
						<InputLabel>Role</InputLabel>
						<Select
							name="role"
							value={formData.role}
							onChange={handleChange}
							label="Role"
							disabled={loading}
						>
							<MenuItem value="user">
								User (Read/Write Access)
							</MenuItem>
							<MenuItem value="admin">
								Admin (Full Access)
							</MenuItem>
						</Select>
					</FormControl>

					<FormControlLabel
						control={
							<Checkbox
								checked={formData.isPartner}
								onChange={(event) =>
									setFormData((prev) => ({
										...prev,
										isPartner: event.target.checked
									}))
								}
								disabled={loading}
							/>
						}
						label="Mark as partner"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={loading}
					startIcon={
						loading ? <CircularProgress size={20} /> : undefined
					}
				>
					{loading ? "Adding..." : "Add User"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddAuthorizedUserDialog;
